<template>
    <input
        :aria-disabled="disabled"
        :checked="value === true"
        class="form-checkbox bg-white rounded shadow-sm | transition-colors duration-100 | focus:outline-none focus:ring-0 focus:ring-offset-0 | focus-visible:ring-2 focus-visible:ring-offset-2"
        :class="[sizeClass, colorClass, cursorClass, { 'cross': type === 'cross' }]"
        data-test="checkbox-input"
        :disabled="disabled"
        :indeterminate.prop="value === null"
        type="checkbox"
        @click.stop="toggle"
        @keyup.space.prevent="toggle"
    >
</template>

<script>
  /**
   * Basic checkbox component.
   * @displayName Checkbox
   */
    export default {
        name: 'ActivixCheckbox',

        model: {
            prop: 'value',
            event: 'input',
        },

        props: {
            value: {
                type: null,
                default: false,
                required: true,
            },
            color: {
                type: String,
                default: 'blue',
                validator: value => ['blue', 'green', 'purple', 'orange', 'red'].includes(value),
            },
            type: {
                type: String,
                default: 'check',
                validator: value => ['check', 'cross'].includes(value),
            },
            tristate: {
                type: Boolean,
                default: false,
            },
            size: {
                type: String,
                default: 'md',
                validator: value => ['sm', 'md', 'lg', 'xl'].includes(value),
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            colorClass() {
                if (this.disabled) {
                    return 'border-gray-200 bg-gray-100 text-gray-300';
                }

                if (this.value === false) {
                    return {
                        green: 'border-gray-300 focus-visible:ring-green-500',
                        orange: 'border-gray-300 focus-visible:ring-orange-500',
                        red: 'border-gray-300 focus-visible:ring-red-500',
                        purple: 'border-gray-300 focus-visible:ring-purple-500',
                        blue: 'border-gray-300 focus-visible:ring-blue-500',
                    }[this.color];
                }

                return {
                    green: 'border-green-500 text-green-500 focus-visible:ring-green-500',
                    orange: 'border-orange-500 text-orange-500 focus-visible:ring-orange-500',
                    red: 'border-red-500 text-red-500 focus-visible:ring-red-500',
                    purple: 'border-purple-500 text-purple-500 focus-visible:ring-purple-500',
                    blue: 'border-blue-500 text-blue-500 focus-visible:ring-blue-500',
                }[this.color];
            },

            sizeClass() {
                return {
                    sm: 'w-4 h-4',
                    md: 'w-5 h-5',
                    lg: 'w-6 h-6',
                    xl: 'w-8 h-8',
                }[this.size];
            },

            cursorClass() {
                return this.disabled ? 'cursor-not-allowed' : 'cursor-pointer';
            },
        },

        methods: {
            toggle(event) {
                if (this.disabled) {
                    event.preventDefault();
                    return;
                }

                if (!this.tristate) {
                    this.$emit('input', !this.value);
                    return;
                }


                switch (this.value) {
                    case null:
                        this.$emit('input', true);
                        break;
                    case true:
                        this.$emit('input', false);
                        break;
                    case false:
                        this.$emit('input', null);
                        break;
                }
            },
        },
    };
</script>

<style scoped>
    .form-checkbox:checked {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M5 13l4 4L19 7' /%3E%3C/svg%3E");
    }
    .form-checkbox.cross:checked {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 18L18 6M6 6l12 12' /%3E%3C/svg%3E");
    }
    .form-checkbox:indeterminate {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M18 12H6' /%3E%3C/svg%3E");
    }
</style>
